import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Upload, 
  FileSpreadsheet,
  Download,
  CheckCircle,
  AlertTriangle,
  Info,
  Users,
  File
} from 'lucide-react';
import { toast } from 'sonner';

const UploadEmployees = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.xlsx') && 
        !selectedFile.name.endsWith('.xls') && 
        !selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      const response = await employeeAPI.bulkUpload(file);
      setUploadResult({
        success: true,
        message: response.data.message,
        employees_added: response.data.employees_added || 0
      });
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create CSV template
    const headers = ['employee_id', 'first_name', 'last_name', 'email', 'phone', 'position', 'department', 'salary', 'role'];
    const sampleRow = ['100051', 'John', 'Doe', 'john.doe@company.com', '+27 81 234 5678', 'Software Developer', 'Engineering', '65000', 'employee'];
    
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  return (
    <div className="space-y-6" data-testid="upload-employees-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <Upload className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Bulk Employee Upload
              </h2>
              <p className="text-emerald-100">
                Import multiple employees from Excel or CSV
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Upload File</CardTitle>
              <CardDescription>Drag and drop or click to select a file</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-input"
                />
                
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileSpreadsheet className="w-8 h-8 text-indigo-600" />
                </div>
                
                <p className="text-lg font-medium text-slate-900 mb-2">
                  {file ? file.name : 'Drop your file here'}
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  or click to browse
                </p>
                <p className="text-xs text-slate-400">
                  Supports: .xlsx, .xls, .csv (max 10MB)
                </p>

                {file && (
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg flex items-center gap-3">
                    <File className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1 text-left">
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Ready</Badge>
                  </div>
                )}
              </div>

              {uploading && (
                <div className="mt-4">
                  <p className="text-sm text-slate-600 mb-2">Uploading...</p>
                  <Progress value={50} className="h-2" />
                </div>
              )}

              {uploadResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  uploadResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-3">
                    {uploadResult.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <p className={`font-medium ${uploadResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                        {uploadResult.message}
                      </p>
                      {uploadResult.employees_added > 0 && (
                        <p className="text-sm text-emerald-600">
                          {uploadResult.employees_added} employees added
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Employees'}
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Info className="w-5 h-5 text-blue-500" />
                Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-600">1</span>
                  </div>
                  <p className="text-sm text-slate-600">Download the template file</p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-600">2</span>
                  </div>
                  <p className="text-sm text-slate-600">Fill in employee details</p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-600">3</span>
                  </div>
                  <p className="text-sm text-slate-600">Upload the completed file</p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-600">4</span>
                  </div>
                  <p className="text-sm text-slate-600">Review and confirm import</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Required Fields
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">employee_id</span>
                  <Badge className="bg-red-100 text-red-700">Required</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">first_name</span>
                  <Badge className="bg-red-100 text-red-700">Required</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">last_name</span>
                  <Badge className="bg-red-100 text-red-700">Required</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">email</span>
                  <Badge className="bg-red-100 text-red-700">Required</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">position</span>
                  <Badge className="bg-slate-100 text-slate-600">Optional</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">department</span>
                  <Badge className="bg-slate-100 text-slate-600">Optional</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">salary</span>
                  <Badge className="bg-slate-100 text-slate-600">Optional</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">role</span>
                  <Badge className="bg-slate-100 text-slate-600">Optional</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Important</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Employee IDs must be unique. Duplicates will be skipped.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UploadEmployees;
